import {
  mergeCountryRates,
  mergeStateRates,
  resolveShippingFee,
  type ShippingCountryRate,
  type ShippingStateRate,
} from "./shipping.js";
import { coerceStoredJsonValue } from "./coerce-stored-json.js";

export const SHIPPING_CONDITION_KINDS = [
  "total_weight_kg_gte",
  "total_weight_kg_lte",
  "product_type_qty_gte",
  "distinct_product_types_gte",
  "physical_units_gte",
] as const;

export type ShippingConditionKind = (typeof SHIPPING_CONDITION_KINDS)[number];

export const SHIPPING_CONDITION_LABELS: Record<ShippingConditionKind, string> = {
  total_weight_kg_gte: "Total weight is at least (kg)",
  total_weight_kg_lte: "Total weight is at most (kg)",
  product_type_qty_gte: "Product type quantity is at least",
  distinct_product_types_gte: "Distinct product types is at least",
  physical_units_gte: "Physical units is at least",
};

export type ShippingCondition =
  | { kind: "total_weight_kg_gte"; kg: number }
  | { kind: "total_weight_kg_lte"; kg: number }
  | { kind: "product_type_qty_gte"; productType: string; qty: number }
  | { kind: "distinct_product_types_gte"; count: number }
  | { kind: "physical_units_gte"; qty: number };

export type ShippingSurchargeRule = {
  id: string;
  label: string;
  fee: number;
  conditions: ShippingCondition[];
};

export type ShippingAdvancedConfig = {
  enabled: boolean;
  rules: ShippingSurchargeRule[];
};

export type ShippingLineInput = {
  weightGrams: number;
  quantity: number;
  productType: string;
};

export type ShippingFeeBreakdown = {
  baseFee: number;
  freeShippingApplied: boolean;
  surcharges: { ruleId: string; label: string; fee: number }[];
  totalFee: number;
};

export const DEFAULT_SHIPPING_ADVANCED_CONFIG: ShippingAdvancedConfig = {
  enabled: false,
  rules: [],
};

function newRuleId(): string {
  return `rule-${Math.random().toString(36).slice(2, 10)}`;
}

function parseCondition(raw: unknown): ShippingCondition | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const kind = String(row.kind ?? "");
  switch (kind) {
    case "total_weight_kg_gte": {
      const kg = Number(row.kg);
      return Number.isFinite(kg) && kg >= 0 ? { kind, kg } : null;
    }
    case "total_weight_kg_lte": {
      const kg = Number(row.kg);
      return Number.isFinite(kg) && kg >= 0 ? { kind, kg } : null;
    }
    case "product_type_qty_gte": {
      const productType = String(row.productType ?? "").trim().toUpperCase();
      const qty = Number(row.qty);
      if (!productType || !Number.isFinite(qty) || qty < 1) return null;
      return { kind, productType, qty: Math.floor(qty) };
    }
    case "distinct_product_types_gte": {
      const count = Number(row.count);
      return Number.isFinite(count) && count >= 1 ? { kind, count: Math.floor(count) } : null;
    }
    case "physical_units_gte": {
      const qty = Number(row.qty);
      return Number.isFinite(qty) && qty >= 1 ? { kind, qty: Math.floor(qty) } : null;
    }
    default:
      return null;
  }
}

function parseRule(raw: unknown): ShippingSurchargeRule | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const fee = Number(row.fee);
  if (!Number.isFinite(fee) || fee < 0) return null;
  const conditions = Array.isArray(row.conditions)
    ? row.conditions.map(parseCondition).filter((c): c is ShippingCondition => c != null)
    : [];
  const label = String(row.label ?? "").trim() || "Surcharge";
  const id = String(row.id ?? "").trim() || newRuleId();
  return { id, label, fee, conditions };
}

export function parseShippingAdvancedConfig(raw: unknown): ShippingAdvancedConfig {
  const value = coerceStoredJsonValue(raw);
  if (!value || typeof value !== "object") return { ...DEFAULT_SHIPPING_ADVANCED_CONFIG };
  const row = value as Record<string, unknown>;
  const rules = Array.isArray(row.rules)
    ? row.rules.map(parseRule).filter((r): r is ShippingSurchargeRule => r != null)
    : [];
  return {
    enabled: Boolean(row.enabled),
    rules,
  };
}

export function buildShippingCartMetrics(lines: ShippingLineInput[]) {
  const totalWeightGrams = lines.reduce(
    (sum, line) => sum + Math.max(0, line.weightGrams) * Math.max(0, line.quantity),
    0,
  );
  const physicalUnits = lines.reduce((sum, line) => sum + Math.max(0, line.quantity), 0);
  const qtyByType = new Map<string, number>();
  for (const line of lines) {
    const key = line.productType.trim().toUpperCase() || "UNKNOWN";
    qtyByType.set(key, (qtyByType.get(key) ?? 0) + Math.max(0, line.quantity));
  }
  return {
    totalWeightKg: totalWeightGrams / 1000,
    physicalUnits,
    qtyByType,
    distinctProductTypes: qtyByType.size,
  };
}

export function conditionMatches(condition: ShippingCondition, lines: ShippingLineInput[]): boolean {
  const metrics = buildShippingCartMetrics(lines);
  switch (condition.kind) {
    case "total_weight_kg_gte":
      return metrics.totalWeightKg >= condition.kg;
    case "total_weight_kg_lte":
      return metrics.totalWeightKg <= condition.kg;
    case "product_type_qty_gte":
      return (metrics.qtyByType.get(condition.productType.toUpperCase()) ?? 0) >= condition.qty;
    case "distinct_product_types_gte":
      return metrics.distinctProductTypes >= condition.count;
    case "physical_units_gte":
      return metrics.physicalUnits >= condition.qty;
    default:
      return false;
  }
}

export function ruleMatches(rule: ShippingSurchargeRule, lines: ShippingLineInput[]): boolean {
  if (!rule.conditions.length) return false;
  return rule.conditions.every((condition) => conditionMatches(condition, lines));
}

export function evaluateAdvancedSurcharges(
  config: ShippingAdvancedConfig,
  lines: ShippingLineInput[],
): { ruleId: string; label: string; fee: number }[] {
  if (!config.enabled || !lines.length) return [];
  const surcharges: { ruleId: string; label: string; fee: number }[] = [];
  for (const rule of config.rules) {
    if (!ruleMatches(rule, lines)) continue;
    surcharges.push({ ruleId: rule.id, label: rule.label, fee: rule.fee });
  }
  return surcharges;
}

export type EvaluateShippingFeeInput = {
  country?: string | null;
  state?: string | null;
  subtotal: number;
  lines: ShippingLineInput[];
  fallbackFee: number;
  freeAbove: number;
  stateRates: ShippingStateRate[];
  countryRates: ShippingCountryRate[];
  advanced: ShippingAdvancedConfig;
};

export function evaluateShippingFee(input: EvaluateShippingFeeInput): ShippingFeeBreakdown {
  const { lines, subtotal, freeAbove, advanced } = input;
  if (!lines.length) {
    return { baseFee: 0, freeShippingApplied: false, surcharges: [], totalFee: 0 };
  }

  const baseFee = resolveShippingFee({
    country: input.country,
    state: input.state,
    countryRates: input.countryRates,
    stateRates: input.stateRates,
    fallbackFee: input.fallbackFee,
    freeAbove: 0,
    subtotal,
  });

  const freeShippingApplied = freeAbove > 0 && subtotal >= freeAbove;
  if (freeShippingApplied) {
    return { baseFee, freeShippingApplied: true, surcharges: [], totalFee: 0 };
  }

  const surcharges = evaluateAdvancedSurcharges(advanced, lines);
  const surchargeTotal = surcharges.reduce((sum, row) => sum + row.fee, 0);
  return {
    baseFee,
    freeShippingApplied: false,
    surcharges,
    totalFee: baseFee + surchargeTotal,
  };
}

export function createEmptyShippingRule(label = "New surcharge"): ShippingSurchargeRule {
  return {
    id: newRuleId(),
    label,
    fee: 0,
    conditions: [{ kind: "physical_units_gte", qty: 1 }],
  };
}
